/**
 * ChatPage - Chat-first entry point for carousel creation.
 *
 * Empty state shows a centered composer + ContentSuggestions.
 * After the user sends a prompt, layout switches to a split
 * (chat left / canvas right on desktop; full-screen takeover on mobile).
 *
 * Generation flows through `useChatCarousel` + `runGenerationPipeline`.
 * Onboarding modals (Instagram profile, avatar) trigger behind the chat
 * via the same cascade used by the legacy DashboardPage.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useUserStatus } from '@/contexts/UserStatusContext';
import { useTranslation } from '@/contexts/LanguageContext';
import { useOnboarding } from '@/contexts/OnboardingContext';
import { getProfile, ProfilePreferences, UserProfile } from '@/services/profileService';
import { processDocument, formatAvatarForPrompt, TEXT_MODEL_PRO } from '@/services/geminiService';
import { analyzeInput, selectModel } from '@/services/modelSelectionService';
import { useChatCarousel } from '@/hooks/useChatCarousel';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { CarouselStyle, UploadedDocument, AvatarData } from '@/types';
import InstagramProfileModal from '@/components/InstagramProfileModal';
import AvatarOnboardingModal from '@/components/AvatarOnboardingModal';
import LoadingScreen from '@/components/ui/loading-screen';
import { ChatLayout } from '@/components/chat/ChatLayout';
import { ChatEmptyState } from '@/components/chat/ChatEmptyState';
import { ChatComposer } from '@/components/chat/ChatComposer';
import { ChatAdvancedOptions, AdvancedOptionsValue } from '@/components/chat/ChatAdvancedOptions';
import { ChatMessageList, Message } from '@/components/chat/ChatMessageList';
import { ChatCanvas } from '@/components/chat/ChatCanvas';
import { getDisplayName, PROFILE_UPDATED_EVENT } from '@/lib/displayName';
import { generateId } from '@/lib/utils';
import { logger } from '@/lib/logger';
import {
  zipSlideElements,
  downloadBlob,
  nativeSlideHeight,
  NATIVE_SLIDE_WIDTH,
} from '@/services/chatExporter';
import TwitterSlide from '@/components/TwitterSlide';
import StorytellerSlide from '@/components/StorytellerSlide';

type LogMessage = Message & { id: string };

const ChatPage: React.FC = () => {
  const location = useLocation();
  const { user } = useAuth();
  const { t } = useTranslation();
  const { isPaywalled, setShowPaywall } = useUserStatus();
  const { notifyChatReady } = useOnboarding();

  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [preferences, setPreferences] = useState<ProfilePreferences | null>(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const [profileRequired, setProfileRequired] = useState(false);
  const [avatarRequired, setAvatarRequired] = useState(false);
  const [profileLoading, setProfileLoading] = useState(true);

  const [composerValue, setComposerValue] = useState('');
  const [attachedDocument, setAttachedDocument] = useState<UploadedDocument | null>(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [lastSentPrompt, setLastSentPrompt] = useState('');
  const [selectedSlideIndex, setSelectedSlideIndex] = useState<number | null>(null);
  const [chatLog, setChatLog] = useState<LogMessage[]>([]);
  // Tracks the pending assistant bubble for the current full-carousel generation
  // so a useEffect watching state transitions knows which bubble to resolve.
  const generationPendingIdRef = useRef<string | null>(null);

  const [isExporting, setIsExporting] = useState(false);
  const slideExportRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [advanced, setAdvanced] = useState<AdvancedOptionsValue>({
    style: CarouselStyle.TWITTER,
    slideCount: 10,
    avatarOverride: null,
  });

  const { state, send, refineSlide } = useChatCarousel();
  const isMobile = useMediaQuery('(max-width: 768px)');

  // The Crisp support bubble is bottom-right fixed and covers the canvas
  // action bar on mobile (blocking the Exportar button). Hide it while the
  // chat-first page is mounted on mobile; restore on unmount.
  useEffect(() => {
    if (!isMobile) return;
    const w = window as unknown as { $crisp?: unknown[] };
    if (!Array.isArray(w.$crisp)) return;
    w.$crisp.push(['do', 'chat:hide']);
    return () => {
      w.$crisp?.push(['do', 'chat:show']);
    };
  }, [isMobile]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user) return;
      try {
        const profile = await getProfile(user.id);
        if (cancelled) return;
        setUserProfile(profile);
        setPreferences(profile?.preferences ?? null);
        if (!profile || !profile.handle?.trim()) {
          setProfileRequired(true);
          setShowProfileModal(true);
        } else if (!profile.preferences?.avatarData) {
          setAvatarRequired(true);
          setShowAvatarModal(true);
        } else {
          notifyChatReady();
        }
      } finally {
        if (!cancelled) setProfileLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, notifyChatReady]);

  // Prefill via ContentSuggestions or any external navigation state
  useEffect(() => {
    const prefill = (location.state as { prefillTopic?: string } | null)?.prefillTopic;
    if (prefill && typeof prefill === 'string') {
      setComposerValue(prefill);
      window.history.replaceState({}, '', location.pathname);
    }
  }, [location]);

  const avatarHandleLabel = userProfile?.handle ? `@${userProfile.handle}` : null;

  /** Append user message + pending assistant bubble, return the pending id. */
  const pushUserAndPending = useCallback((userText: string, pendingText: string): string => {
    const userId = generateId();
    const pendingId = generateId();
    setChatLog((prev) => [
      ...prev,
      { id: userId, role: 'user', content: userText },
      { id: pendingId, role: 'assistant', content: pendingText, status: 'pending' },
    ]);
    return pendingId;
  }, []);

  /** Resolve a pending assistant bubble with new content (or error text). */
  const resolvePending = useCallback((pendingId: string, content: string, isError = false) => {
    setChatLog((prev) =>
      prev.map((m) =>
        m.id === pendingId
          ? { ...m, content, status: 'done' as const, ...(isError ? { role: 'assistant' as const } : {}) }
          : m,
      ),
    );
  }, []);

  // Resolve the full-carousel pending bubble when generation settles.
  useEffect(() => {
    const pendingId = generationPendingIdRef.current;
    if (!pendingId) return;
    if (state.status === 'ready') {
      resolvePending(pendingId, 'Carrossel pronto. Confira no canvas →');
      generationPendingIdRef.current = null;
    } else if (state.status === 'error') {
      resolvePending(pendingId, `Não consegui gerar: ${state.message}`, true);
      generationPendingIdRef.current = null;
    }
  }, [state, resolvePending]);

  const submit = async () => {
    if (!user) return;
    if (isPaywalled) {
      setShowPaywall(true);
      return;
    }

    // Targeted refinement: a slide is mentioned — refine only that slide.
    if (selectedSlideIndex !== null && state.status === 'ready') {
      const instruction = composerValue.trim();
      if (!instruction) return;
      const targetIdx = selectedSlideIndex;

      const pendingId = pushUserAndPending(
        `Slide ${targetIdx + 1}: ${instruction}`,
        `Ajustando slide ${targetIdx + 1}`,
      );
      setLastSentPrompt(`Slide ${targetIdx + 1}: ${instruction}`);
      setComposerValue('');
      setSelectedSlideIndex(null);

      try {
        await refineSlide(targetIdx, instruction);
        resolvePending(pendingId, `Slide ${targetIdx + 1} atualizado.`);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Erro desconhecido';
        resolvePending(pendingId, `Não consegui ajustar o slide ${targetIdx + 1}: ${msg}`, true);
      }
      return;
    }

    if (!composerValue.trim() && !attachedDocument) return;

    const analysis = analyzeInput(composerValue, attachedDocument);
    const modelSelection = selectModel(analysis);
    const avatarContext =
      advanced.avatarOverride ??
      (preferences?.avatarData ? formatAvatarForPrompt(preferences.avatarData) : '');

    const snapshotPrompt = composerValue.trim() || attachedDocument?.name || '';
    setLastSentPrompt(snapshotPrompt);

    generationPendingIdRef.current = pushUserAndPending(
      snapshotPrompt,
      'Gerando seu carrossel',
    );

    send({
      prompt: composerValue,
      document: attachedDocument ?? undefined,
      style: advanced.style,
      slideCount: advanced.slideCount,
      modelName: modelSelection.model || TEXT_MODEL_PRO,
      avatarContext: avatarContext || undefined,
      profile: {
        name: userProfile?.name || '',
        handle: userProfile?.handle || '',
        avatarUrl: userProfile?.avatarUrl || '',
      },
    });

    setComposerValue('');
    setAttachedDocument(null);
    setAdvancedOpen(false);
    setSelectedSlideIndex(null);
  };

  const handleAttach = async (file: File) => {
    try {
      const doc = await processDocument(file);
      setAttachedDocument(doc);
    } catch {
      /* toast UI omitted in v1 MVP, matches CreatePage pattern */
    }
  };

  const messages: Message[] = chatLog;

  if (profileLoading) return <LoadingScreen message={t('dashboard.loadingProjects')} />;

  const isEmpty = state.status === 'empty';

  // "Regerar tudo" re-uses the last sent prompt and the current advanced
  // options to fire a fresh full-carousel generation. Clears any slide
  // selection so the next dispatch isn't treated as a refinement.
  const regenerateAll = () => {
    if (!user) return;
    if (isPaywalled) {
      setShowPaywall(true);
      return;
    }
    const basePrompt = lastSentPrompt.trim();
    if (!basePrompt) return;

    setSelectedSlideIndex(null);

    const analysis = analyzeInput(basePrompt, null);
    const modelSelection = selectModel(analysis);
    const avatarContext =
      advanced.avatarOverride ??
      (preferences?.avatarData ? formatAvatarForPrompt(preferences.avatarData) : '');

    generationPendingIdRef.current = pushUserAndPending(
      'Regerar tudo',
      'Gerando seu carrossel',
    );

    send({
      prompt: basePrompt,
      document: undefined,
      style: advanced.style,
      slideCount: advanced.slideCount,
      modelName: modelSelection.model || TEXT_MODEL_PRO,
      avatarContext: avatarContext || undefined,
      profile: {
        name: userProfile?.name || '',
        handle: userProfile?.handle || '',
        avatarUrl: userProfile?.avatarUrl || '',
      },
    });
  };

  const chatPane = (
    <>
      <ChatMessageList messages={messages} />
      <div className="border-t border-border p-3 space-y-2">
        <ChatComposer
          value={composerValue}
          onChange={setComposerValue}
          onSubmit={submit}
          isBusy={state.status === 'generating'}
          attachedDocument={attachedDocument}
          onAttach={handleAttach}
          onDetach={() => setAttachedDocument(null)}
          onToggleAdvanced={() => setAdvancedOpen((x) => !x)}
          placeholder="Regerar tudo com um novo prompt…"
          variant="inline"
          mentionedSlideIndex={selectedSlideIndex}
          onRemoveMention={() => setSelectedSlideIndex(null)}
        />
        {advancedOpen && (
          <ChatAdvancedOptions
            value={advanced}
            onChange={setAdvanced}
            avatarHandleLabel={avatarHandleLabel}
            onEditAvatar={() => setShowAvatarModal(true)}
          />
        )}
      </div>
    </>
  );

  const handleExport = async () => {
    if (state.status !== 'ready') return;
    if (isExporting) return;
    const project = state.project;

    setIsExporting(true);
    try {
      // Let React mount the hidden render, then give the DOM <img> tags a
      // beat to start fetching. zipSlideElements waits for each to fully
      // load + inlines external URLs before capturing, so the heavy lifting
      // is in there — this just covers React's commit tick.
      await new Promise((resolve) => window.setTimeout(resolve, 200));

      const elements = project.slides
        .map((s) => slideExportRefs.current[s.id])
        .filter((el): el is HTMLDivElement => !!el);

      if (elements.length === 0) {
        logger.error('Chat export: no slide elements to capture');
        return;
      }

      const zipBlob = await zipSlideElements(elements, {
        width: NATIVE_SLIDE_WIDTH,
        height: nativeSlideHeight(project.aspectRatio),
        bgColor: project.theme === 'DARK' ? '#000000' : '#FFFFFF',
      });
      const filename = `${project.name || 'carousel'}.zip`.replace(/[^\w.\- ]+/g, '_');
      downloadBlob(zipBlob, filename);
    } catch (err) {
      logger.error('Chat export failed');
    } finally {
      setIsExporting(false);
    }
  };

  const canvasPane =
    state.status === 'ready' ? (
      <ChatCanvas
        project={state.project}
        imageJobs={state.imageJobs}
        selectedSlideIndex={selectedSlideIndex}
        onSelectSlide={(idx) =>
          setSelectedSlideIndex((prev) => (prev === idx ? null : idx))
        }
        onRegenerateAll={regenerateAll}
        onExport={handleExport}
        isExporting={isExporting}
      />
    ) : (
      <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
        Envie sua primeira mensagem pra começar.
      </div>
    );

  return (
    <>
      <ChatLayout
        isEmpty={isEmpty}
        canvasReady={state.status === 'ready'}
        empty={
          <ChatEmptyState
            greetingName={getDisplayName(userProfile, user) || ''}
            composerValue={composerValue}
            onComposerChange={setComposerValue}
            onSubmit={submit}
            isBusy={state.status === 'generating'}
            attachedDocument={attachedDocument}
            onAttach={handleAttach}
            onDetach={() => setAttachedDocument(null)}
            advancedOpen={advancedOpen}
            onToggleAdvanced={() => setAdvancedOpen((x) => !x)}
            advancedValue={advanced}
            onAdvancedChange={setAdvanced}
            avatarHandleLabel={avatarHandleLabel}
            onEditAvatar={() => setShowAvatarModal(true)}
            userProfile={userProfile}
            preferences={preferences}
            userId={user?.id || ''}
            onSuggestionPick={(topic) => setComposerValue(topic)}
            onPreferencesUpdated={(prefs) => setPreferences(prefs)}
          />
        }
        chatPane={chatPane}
        canvasPane={canvasPane}
      />

      {/*
        Hidden off-screen render of the slides at native 1080px width so
        html-to-image can capture them for ZIP export. Mounted only while
        exporting to avoid paying the layout cost during normal use.
      */}
      {isExporting && state.status === 'ready' && (() => {
        const project = state.project;
        const SlideComponent =
          project.style === CarouselStyle.STORYTELLER ? StorytellerSlide : TwitterSlide;
        const width = NATIVE_SLIDE_WIDTH;
        const height = nativeSlideHeight(project.aspectRatio);
        return (
          <div
            aria-hidden="true"
            style={{
              position: 'fixed',
              top: '-20000px',
              left: 0,
              pointerEvents: 'none',
              zIndex: -1,
            }}
          >
            {project.slides.map((slide, idx) => (
              <div
                key={slide.id}
                ref={(el) => {
                  slideExportRefs.current[slide.id] = el;
                }}
                style={{
                  width: `${width}px`,
                  height: `${height}px`,
                  backgroundColor: project.theme === 'DARK' ? '#000000' : '#FFFFFF',
                }}
              >
                <SlideComponent
                  slide={slide}
                  profile={project.profile}
                  index={idx}
                  total={project.slides.length}
                  showSlideNumbers={project.showSlideNumbers}
                  headerScale={project.headerScale}
                  theme={project.theme}
                  showVerifiedBadge={project.showVerifiedBadge}
                  accentColor={project.accentColor}
                  fontStyle={project.fontStyle}
                  fontScale={project.fontScale}
                  layoutSettings={project.layoutSettings}
                  forExport
                />
              </div>
            ))}
          </div>
        );
      })()}

      <InstagramProfileModal
        open={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        onProfileSaved={(savedProfile) => {
          setUserProfile(savedProfile);
          setPreferences(savedProfile.preferences || null);
          window.dispatchEvent(new Event(PROFILE_UPDATED_EVENT));
          setProfileRequired(false);
          if (!savedProfile.preferences?.avatarData) {
            setAvatarRequired(true);
            setShowAvatarModal(true);
          } else {
            notifyChatReady();
          }
        }}
        required={profileRequired}
        userId={user?.id || ''}
      />

      <AvatarOnboardingModal
        open={showAvatarModal}
        onClose={() => setShowAvatarModal(false)}
        onAvatarSaved={(avatar: AvatarData) => {
          setPreferences((prev) =>
            prev
              ? ({ ...prev, avatarData: avatar } as ProfilePreferences)
              : ({ avatarData: avatar } as ProfilePreferences),
          );
          setAvatarRequired(false);
          notifyChatReady();
        }}
        required={avatarRequired}
        userId={user?.id || ''}
        initialData={preferences?.avatarData}
      />
    </>
  );
};

export default ChatPage;
